import { useState, useEffect, useCallback, useRef } from "react";
import { MediaModel } from "app/models";
import { useActions } from "app/utils/hooks";
import { fetchMediaById } from "app/actions/media-library-data";

interface IUseAttachments {
  initialAttachments: MediaModel[];
  multiSelect?: boolean;
}

export function useAttachments({
  initialAttachments = [],
  multiSelect = true,
}: IUseAttachments) {
  const [attachments, setAttachments] =
    useState<MediaModel[]>(initialAttachments);
  const mediaActions = useActions({ fetchMediaById });
  const pollingTimersRef = useRef<NodeJS.Timeout[]>([]);

  const attachmentsTags = [
    ...new Set(
      attachments
        ?.flatMap((att) => att.tags || [])
        .filter((tag) => typeof tag === "string" && tag.trim() !== ""),
    ),
  ];

  const checkIfIsMultiSelect = useCallback(
    (media: MediaModel[]) => {
      if (!multiSelect) {
        return setAttachments(media);
      }

      return setAttachments((prev) => {
        const existingIds = new Set((prev || []).map((m) => m.id));
        const newMedia = media.filter((m) => !existingIds.has(m.id));
        return [...(prev || []), ...newMedia];
      });
    },
    [multiSelect],
  );

  const onSelectMedia = useCallback(
    (media: MediaModel[]) => {
      checkIfIsMultiSelect(media);
      const tags = [
        ...new Set(
          media
            ?.flatMap((att) => att.tags || [])
            .filter((tag) => typeof tag === "string" && tag.trim() !== ""),
        ),
      ];
      return tags;
    },
    [checkIfIsMultiSelect],
  );

  const onClearMedia = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((media) => media.id !== id));
  }, []);

  const clearAllTimers = useCallback(() => {
    pollingTimersRef.current.forEach(clearInterval);
    pollingTimersRef.current = [];
  }, []);

  useEffect(() => {
    clearAllTimers();

    if (!attachments || attachments.length === 0) {
      return undefined;
    }

    const isAttachmentProcessed = (att: MediaModel) =>
      att.processed ||
      (Array.isArray(att.thumbnails) && att.thumbnails.length > 0);

    const unprocessedAttachments = attachments.filter(
      (att) => !isAttachmentProcessed(att),
    );

    if (unprocessedAttachments.length === 0) {
      return undefined;
    }

    const pollAttachment = async (attachmentId: string) => {
      try {
        const res = await mediaActions.fetchMediaById(attachmentId);
        if (res?.processed) {
          setAttachments((prev) => {
            const updated = [...prev];
            const idx = updated.findIndex((m) => m.id === attachmentId);
            if (idx !== -1) {
              updated[idx] = { ...updated[idx], ...res };
            }
            return updated;
          });
        }
      } catch (error) {
        console.error(`Failed to fetch media ${attachmentId}:`, error);
      }
    };

    const timer = setInterval(() => {
      setAttachments((currentAttachments) => {
        currentAttachments
          .filter(
            (att) =>
              !isAttachmentProcessed(att) &&
              (att.type === "video" || !att.processed),
          )
          .forEach((att) => pollAttachment(att.id));
        return currentAttachments;
      });
    }, 3000);

    pollingTimersRef.current.push(timer);

    unprocessedAttachments
      .filter((att) => att.type === "video" || !att.processed)
      .forEach((att) => pollAttachment(att.id));

    return clearAllTimers;
  }, [attachments, mediaActions, clearAllTimers]);

  useEffect(() => {
    return clearAllTimers;
  }, [clearAllTimers]);

  return {
    attachments,
    onSelectMedia,
    onClearMedia,
    setAttachments,
    attachmentsTags,
  };
}
